import React, { useEffect, useState, useMemo } from 'react';
import { Table, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import type { SortingFn } from '@tanstack/react-table';
import {
  fetchCaseActivityLog,
  createCaseActivityLogEntry,
  updateCaseActivityLogEntry,
  deleteCaseActivityLogEntry,
} from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';

interface ActivityLogEntry {
  timestamp: string;
  entry_id: string;
  entry_date: string;
  note_text: string;
  created_by: string;
  court_event_id?: string;
}

const isCourtSyncEntry = (entry: ActivityLogEntry): boolean =>
  !!entry.court_event_id || entry.created_by === 'Court Docket Sync';

// entry_date may be YYYY-MM-DD or, for older/legacy entries, other formats;
// parse into a real Date so sorting is chronological, not lexical.
const parseEntryDate = (entry_date: string): Date => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(entry_date || '');
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(entry_date);
  return isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const formatEntryDate = (entry_date: string): string => {
  return parseEntryDate(entry_date).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const sortByEntryDate: SortingFn<ActivityLogEntry> = (rowA, rowB) => {
  return parseEntryDate(rowA.original.entry_date).getTime() - parseEntryDate(rowB.original.entry_date).getTime();
};

const columnHelper = createColumnHelper<ActivityLogEntry>();

const CaseActivityLog: React.FC = () => {
  const { case_id } = useParams<{ case_id: string }>();
  const auth = useAuth();

  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'entry_date', desc: true }]);
  const [showCourtEvents, setShowCourtEvents] = useState(true);

  const [noteDate, setNoteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [editingTimestamp, setEditingTimestamp] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editDate, setEditDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingTimestamp, setDeletingTimestamp] = useState<string | null>(null);

  const loadEntries = async () => {
    try {
      const response = await fetchCaseActivityLog(case_id!, auth.user?.id_token);
      setEntries(response.data.activity_log);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.id_token) {
      loadEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [case_id, auth.user?.id_token]);

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteText.trim() || submitting) return;

    setSubmitting(true);
    try {
      await createCaseActivityLogEntry(case_id!, noteText.trim(), auth.user?.id_token, noteDate);
      setNoteText('');
      await loadEntries();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (entry: ActivityLogEntry) => {
    setEditingTimestamp(entry.timestamp);
    setEditText(entry.note_text);
    setEditDate(entry.entry_date);
  };

  const cancelEditing = () => {
    setEditingTimestamp(null);
    setEditText('');
    setEditDate('');
  };

  const saveEdit = async (timestamp: string) => {
    if (!editText.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      await updateCaseActivityLogEntry(case_id!, timestamp, editText.trim(), auth.user?.id_token, editDate);
      cancelEditing();
      await loadEntries();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (timestamp: string) => {
    if (!window.confirm('Delete this activity log entry? This cannot be undone.')) return;
    setDeletingTimestamp(timestamp);
    try {
      await deleteCaseActivityLogEntry(case_id!, timestamp, auth.user?.id_token);
      await loadEntries();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingTimestamp(null);
    }
  };

  const columns = useMemo(() => [
    columnHelper.accessor('entry_date', {
      header: 'Date',
      cell: info => <span className="small text-nowrap">{formatEntryDate(info.getValue())}</span>,
      sortingFn: sortByEntryDate,
      size: 100,
    }),
    columnHelper.accessor('note_text', {
      header: 'Note',
      cell: info => {
        const entry = info.row.original;
        if (editingTimestamp === entry.timestamp) {
          return (
            <div className="d-flex gap-2 align-items-start py-1">
              <Form.Control
                type="date"
                size="sm"
                style={{ maxWidth: 150 }}
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
              />
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
              />
            </div>
          );
        }
        return <span className="small" style={{ whiteSpace: 'pre-wrap' }}>{entry.note_text}</span>;
      },
    }),
    columnHelper.accessor('created_by', {
      header: 'By',
      cell: info => <span className="small text-muted text-nowrap">{info.getValue()}</span>,
      size: 120,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 120,
      cell: info => {
        const entry = info.row.original;
        const isEditing = editingTimestamp === entry.timestamp;
        const isDeleting = deletingTimestamp === entry.timestamp;
        if (isCourtSyncEntry(entry)) {
          return <span className="text-muted small fst-italic">Court Docket</span>;
        }
        if (isEditing) {
          return (
            <div className="d-flex gap-1 flex-nowrap">
              <Button size="sm" variant="success" disabled={!editText.trim() || savingEdit} onClick={() => saveEdit(entry.timestamp)}>
                {savingEdit ? '...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={cancelEditing} disabled={savingEdit}>
                Cancel
              </Button>
            </div>
          );
        }
        return (
          <div className="d-flex gap-2 flex-nowrap">
            <Button variant="link" size="sm" className="p-0" onClick={() => startEditing(entry)}>
              Edit
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-danger"
              disabled={isDeleting}
              onClick={() => handleDelete(entry.timestamp)}
            >
              {isDeleting ? '...' : 'Delete'}
            </Button>
          </div>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [editingTimestamp, editText, editDate, savingEdit, deletingTimestamp]);

  const visibleEntries = useMemo(
    () => showCourtEvents ? entries : entries.filter(e => !isCourtSyncEntry(e)),
    [entries, showCourtEvents]
  );

  const table = useReactTable({
    data: visibleEntries,
    columns,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
  });

  return (
    <div>
      <style>{`.court-sync-row > * { background-color: #e9ecef !important; }`}</style>
      <Form onSubmit={handleAddNote} className="d-flex gap-2 align-items-start mb-3">
        <Form.Control
          type="date"
          size="sm"
          style={{ maxWidth: 150 }}
          value={noteDate}
          onChange={(e) => setNoteDate(e.target.value)}
        />
        <Form.Control
          as="textarea"
          rows={1}
          size="sm"
          placeholder="Add a note (e.g. a call log entry, status update)..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
        <Button type="submit" variant="primary" size="sm" disabled={!noteText.trim() || submitting}>
          {submitting ? 'Adding...' : 'Add'}
        </Button>
      </Form>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading activity log...</span>
        </div>
      ) : entries.length === 0 ? (
        <Alert variant="secondary" className="mb-0">No activity log entries yet.</Alert>
      ) : (
        <>
          <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
            <InputGroup size="sm" style={{ maxWidth: 300 }}>
              <InputGroup.Text>🔍</InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search notes..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </InputGroup>
            <Form.Check
              type="switch"
              id="show-court-events-switch"
              label="Show court docket events"
              checked={showCourtEvents}
              onChange={(e) => setShowCourtEvents(e.target.checked)}
              className="small"
            />
          </div>
          <div className="table-responsive">
            <Table striped hover size="sm" className="mb-0">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className={isCourtSyncEntry(row.original) ? 'court-sync-row' : undefined}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {table.getFilteredRowModel().rows.length === 0 && (
            <Alert variant="secondary" className="mb-0">No notes match your search.</Alert>
          )}
        </>
      )}
    </div>
  );
};

export default CaseActivityLog;
